/* OpenAI Ads attribution -> Shopify cart attributes
 * Purpose: preserve approved ad click params before normal checkout or Shop Pay.
 * Privacy: allow-list only, no raw query strings, no console logging of values.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'dv_oai_attribution_v1';
  var MAX_VALUE_LENGTH = 256;
  var WRITE_DEBOUNCE_MS = 300;
  var PARAM_TO_ATTRIBUTE = {
    utm_source: '_oai_utm_source',
    utm_medium: '_oai_utm_medium',
    utm_campaign: '_oai_utm_campaign',
    utm_content: '_oai_utm_content',
    utm_term: '_oai_utm_term',
    oai_ad_account_id: '_oai_ad_account_id',
    oai_campaign_id: '_oai_campaign_id',
    oai_ad_group_id: '_oai_ad_group_id',
    oai_ad_id: '_oai_ad_id',
    oppref: '_oai_oppref',
    olref: '_oai_olref',
    oppcref: '_oai_oppcref'
  };
  var COOKIE_TO_ATTRIBUTE = {
    __oppref: '_oai_oppref',
    __olref: '_oai_olref',
    __oppcref: '_oai_oppcref'
  };

  var writeTimer = null;
  var lastWriteBody = '';

  function getStorage(storageName) {
    try {
      var storage = window[storageName];
      var testKey = STORAGE_KEY + '_test';
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return storage;
    } catch (error) {
      return null;
    }
  }

  function readStoredAttributes() {
    var result = {};
    var storage = getStorage('sessionStorage');
    if (!storage) return result;

    try {
      var parsed = JSON.parse(storage.getItem(STORAGE_KEY) || '{}');
      Object.keys(PARAM_TO_ATTRIBUTE).forEach(function (param) {
        var attrName = PARAM_TO_ATTRIBUTE[param];
        var value = sanitizeValue(parsed[attrName]);
        if (value) result[attrName] = value;
      });
    } catch (error) {
      storage.removeItem(STORAGE_KEY);
    }

    return result;
  }

  function saveStoredAttributes(attributes) {
    var storage = getStorage('sessionStorage');
    if (!storage) return;

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(attributes));
    } catch (error) {
      /* Storage can fail in private browsing. Attribution must not block checkout. */
    }
  }

  function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  function isPlainScalar(value) {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
  }

  function hasLuhnCheck(value) {
    var digits = String(value).replace(/\D/g, '');
    if (digits.length < 13 || digits.length > 19) return false;

    var sum = 0;
    var shouldDouble = false;
    for (var i = digits.length - 1; i >= 0; i--) {
      var digit = parseInt(digits.charAt(i), 10);
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  }

  function looksLikeSensitiveValue(value) {
    var text = String(value);
    var lowered = text.toLowerCase();
    var digitCount = (text.match(/\d/g) || []).length;

    if (/[\u0000-\u001F\u007F]/.test(text)) return true;
    if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(text)) return true;
    if (/[?&#]/.test(text)) return true;
    if (/(checkout|payment|card|customer|email|phone|address)[\w -]{0,24}[:=]/i.test(text)) return true;
    if (/\b\d{1,6}\s+[a-z0-9 .'-]+\s+(street|st|avenue|ave|road|rd|drive|dr|lane|ln|boulevard|blvd|court|ct|circle|cir|way)\b/i.test(text)) return true;
    if (digitCount >= 8 && /[+().\-\s]/.test(text) && /^\+?[\d().\-\s]+$/.test(text)) return true;
    if (hasLuhnCheck(text)) return true;
    if (lowered.indexOf('gid://shopify/checkout') !== -1) return true;
    if (lowered.indexOf('gid://shopify/customer') !== -1) return true;

    return false;
  }

  function sanitizeValue(value) {
    if (!isPlainScalar(value)) return '';

    var text = String(value).trim();
    if (!text) return '';
    if (looksLikeSensitiveValue(text)) return '';
    if (text.length > MAX_VALUE_LENGTH) text = text.slice(0, MAX_VALUE_LENGTH);

    return text;
  }

  function readUrlAttributes() {
    var attributes = {};
    var search = window.location && window.location.search ? window.location.search : '';
    if (!search) return attributes;

    var params;
    try {
      params = new URLSearchParams(search);
    } catch (error) {
      return attributes;
    }

    Object.keys(PARAM_TO_ATTRIBUTE).forEach(function (param) {
      if (!params.has(param)) return;
      var value = sanitizeValue(params.get(param));
      if (value) attributes[PARAM_TO_ATTRIBUTE[param]] = value;
    });

    return attributes;
  }

  function mergeAttributes(base, update) {
    var merged = {};
    Object.keys(base || {}).forEach(function (key) {
      if (typeof base[key] === 'string' && base[key]) merged[key] = base[key];
    });
    Object.keys(update || {}).forEach(function (key) {
      if (typeof update[key] === 'string' && update[key]) merged[key] = update[key];
    });
    return merged;
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(String(value).replace(/\+/g, ' '));
    } catch (error) {
      return value;
    }
  }

  function readCookieAttributes() {
    var attributes = {};
    var cookieText = document.cookie || '';
    if (!cookieText) return attributes;

    cookieText.split(';').forEach(function (cookie) {
      var parts = cookie.split('=');
      var name = String(parts.shift() || '').trim();
      if (!hasOwn(COOKIE_TO_ATTRIBUTE, name)) return;

      var value = sanitizeValue(safeDecode(parts.join('=')));
      if (value) attributes[COOKIE_TO_ATTRIBUTE[name]] = value;
    });

    return attributes;
  }

  function collectAttribution() {
    var stored = readStoredAttributes();
    var fromCookies = readCookieAttributes();
    var fromUrl = readUrlAttributes();
    var merged = mergeAttributes(mergeAttributes(stored, fromCookies), fromUrl);

    if (attributeCount(merged)) {
      saveStoredAttributes(merged);
    }

    return merged;
  }

  function attributeCount(attributes) {
    return Object.keys(attributes || {}).length;
  }

  function cartUpdateUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root.replace(/\/?$/, '/') + 'cart/update.js';
    }
    return '/cart/update.js';
  }

  function writeAttributes(reason) {
    var attributes = collectAttribution();
    if (!attributeCount(attributes) || typeof fetch !== 'function') {
      return Promise.resolve(false);
    }

    var body = JSON.stringify({ attributes: attributes });
    if (body === lastWriteBody && reason !== 'checkout') {
      return Promise.resolve(false);
    }
    lastWriteBody = body;

    try {
      return fetch(cartUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: body
      }).then(function () {
        return true;
      }).catch(function () {
        return false;
      });
    } catch (error) {
      return Promise.resolve(false);
    }
  }

  function scheduleWrite(reason, delay) {
    if (writeTimer) {
      window.clearTimeout(writeTimer);
    }
    writeTimer = window.setTimeout(function () {
      writeTimer = null;
      writeAttributes(reason);
    }, typeof delay === 'number' ? delay : WRITE_DEBOUNCE_MS);
  }

  function captureAndStoreAttribution() {
    if (attributeCount(collectAttribution())) {
      scheduleWrite('page-load', 0);
    }
  }

  function textIncludesCheckoutSignal(value) {
    var text = String(value || '').toLowerCase();
    return text.indexOf('checkout') !== -1 || text.indexOf('shop pay') !== -1 || text.indexOf('shoppay') !== -1;
  }

  function isCheckoutIntent(target) {
    var node = target;
    var hops = 0;
    while (node && node !== document && hops < 8) {
      if (node.getAttribute) {
        var name = node.getAttribute('name');
        var id = node.getAttribute('id');
        var className = node.getAttribute('class');
        var ariaLabel = node.getAttribute('aria-label');
        var href = node.getAttribute('href');
        var testId = node.getAttribute('data-testid');

        if (name === 'checkout') return true;
        if (href && (/\/checkout\b|\/checkouts\b|shop_pay|shoppay/i).test(href)) return true;
        if (textIncludesCheckoutSignal(id)) return true;
        if (textIncludesCheckoutSignal(className)) return true;
        if (textIncludesCheckoutSignal(ariaLabel)) return true;
        if (textIncludesCheckoutSignal(testId)) return true;
        if (className && /shopify-payment-button|additional-checkout-buttons/i.test(className)) return true;
      }
      node = node.parentNode;
      hops += 1;
    }
    return false;
  }

  function bindCartUpdateEvent() {
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          scheduleWrite('cart-update', 0);
        });
      }
    } catch (error) {
      /* Pub/sub is optional. */
    }
  }

  function bindCheckoutHooks() {
    var writeBeforeNavigation = function (event) {
      if (event && isCheckoutIntent(event.target)) {
        writeAttributes('checkout');
      }
    };

    document.addEventListener('pointerdown', writeBeforeNavigation, true);
    document.addEventListener('click', writeBeforeNavigation, true);
    document.addEventListener('submit', function (event) {
      var form = event.target;
      var action = form && form.getAttribute ? form.getAttribute('action') : '';
      if (isCheckoutIntent(document.activeElement) || /\/cart\b|\/checkout\b|\/checkouts\b/i.test(action || '')) {
        writeAttributes('checkout');
      }
    }, true);
  }

  captureAndStoreAttribution();
  bindCartUpdateEvent();
  bindCheckoutHooks();
})();
